import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, MapPin, DollarSign, Eye, MessageCircle, Star, Filter } from 'lucide-react';
import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api-client';
import type { PackageListing, FarmingDomain } from '@shared/types';

export function MarketplacePackagesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [domainFilter, setDomainFilter] = useState<string>('all');
  const [statusFilter] = useState<string>('active');

  const { data: listings, isLoading } = useQuery({
    queryKey: ['marketplace-packages', domainFilter, statusFilter],
    queryFn: () => {
      let url = '/api/listings/packages?status=' + statusFilter;
      if (domainFilter !== 'all') {
        url += '&domain=' + domainFilter;
      }
      return api<PackageListing[]>(url);
    },
  });

  // Filter by search query client-side
  const filteredListings = listings?.filter(listing => 
    searchQuery === '' || 
    listing.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    listing.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Package Listings</h1>
          <p className="text-muted-foreground">
            Browse complete farm startup packages from verified sellers
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search packages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={domainFilter} onValueChange={setDomainFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="livestock">Livestock</SelectItem>
              <SelectItem value="crop">Crop</SelectItem>
              <SelectItem value="aquaculture">Aquaculture</SelectItem>
              <SelectItem value="mixed-farm">Mixed Farm</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Listings Grid */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading packages...</p>
          </div>
        ) : filteredListings && filteredListings.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredListings.map((listing) => (
              <PackageListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        ) : (
          <Card className="py-12">
            <CardContent className="text-center">
              <p className="text-muted-foreground">No packages found. Try adjusting your filters.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

interface PackageListingCardProps {
  listing: PackageListing;
}

function PackageListingCard({ listing }: PackageListingCardProps) {
  const statusBadge = {
    active: <Badge variant="default">Active</Badge>,
    sold: <Badge variant="secondary">Sold</Badge>,
    expired: <Badge variant="secondary">Expired</Badge>,
    pending_review: <Badge variant="outline">Pending Review</Badge>,
    draft: <Badge variant="outline">Draft</Badge>,
    flagged: <Badge variant="destructive">Flagged</Badge>,
    removed: <Badge variant="destructive">Removed</Badge>,
  }[listing.status];

  const domainLabel = {
    livestock: 'Livestock',
    crop: 'Crop',
    aquaculture: 'Aquaculture',
    'mixed-farm': 'Mixed Farm',
  }[listing.farmingDomain];

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between mb-2">
          <Badge variant="secondary">{domainLabel}</Badge>
          {listing.featured && <Badge className="ml-2">Featured</Badge>}
        </div>
        <CardTitle className="text-xl line-clamp-2">{listing.title}</CardTitle>
        <CardDescription className="line-clamp-2">{listing.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-lg font-semibold">
            <DollarSign className="h-4 w-4" />
            {listing.currency === 'GHS' ? '₵' : listing.currency} {listing.price.toLocaleString()}
          </div>
          {listing.negotiable && (
            <Badge variant="outline" className="text-xs">Negotiable</Badge>
          )}
        </div>
        
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4" />
          {listing.location}
        </div>

        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Eye className="h-4 w-4" />
            {listing.views}
          </span>
          <span className="flex items-center gap-1">
            <MessageCircle className="h-4 w-4" />
            {listing.inquiries}
          </span>
        </div>

        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground mb-2">Package includes:</p>
          <ul className="space-y-1">
            {listing.items.slice(0, 3).map((item, idx) => (
              <li key={idx} className="text-sm flex items-start">
                <span className="mr-1">•</span>
                <span className="line-clamp-1">{item.quantity}x {item.name}</span>
              </li>
            ))}
            {listing.items.length > 3 && (
              <li className="text-xs text-muted-foreground">+{listing.items.length - 3} more items</li>
            )}
          </ul>
        </div>
      </CardContent>
      <CardFooter className="flex gap-2">
        <Button className="flex-1" size="sm">View Details</Button>
        <Button variant="outline" size="sm">
          <MessageCircle className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
}
